import { quat } from 'gl-matrix';
import { Scene } from '../Scene';
import { SceneManifest, SceneObjectDef, TransformDef } from '../SceneManifest';
import { ResolvedPrimitive } from '../../gltf';
import {
  buildAttachmentOffsetMatrix,
  composeAttachmentOffsetMatrix,
  readAttachmentBoneDefaults,
  readJointAttachments,
  readMeshTransform,
  readModelTransform,
} from '../../gltf';
import { GltfMesh } from '../../mesh';
import { GltfAnimationDataTexture } from '../../texture';
import { GltfAnimation } from '../../animation';
import { createAnimationState } from '../../animation/AnimationStateFactory';
import { GltfAttachmentHolder } from '../../object';
import { RenderObject } from '../../object/RenderObject';
import { Material } from '../../material';
import { ParsedModel } from './ModelLoader';
import { composeWorldRotation } from '../../util/PlacementComposer';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const warnedNonUuid = new Set<string>();

function warnIfNotUuid(value: string, context: string): void {
  if (UUID_RE.test(value)) return;
  if (warnedNonUuid.has(value)) return;
  warnedNonUuid.add(value);
  console.warn(
    `[scene-manifest] ${context}: '${value}' is not a UUID — manifest may be pre-migration`,
  );
}

export async function loadObjects(
  scene: Scene,
  manifest: SceneManifest,
  modelCache: Map<string, ParsedModel>,
): Promise<Map<string, RenderObject[]>> {
  const namedObjects = new Map<string, RenderObject[]>();
  // Cache shared meshes for static objects of the same model+meshName.
  const meshCache = new Map<string, GltfMesh>();

  const objectDefs = manifest.globalStaticObjects ?? [];
  for (const objDef of objectDefs) {
    const model = modelCache.get(objDef.model);
    if (!model) {
      const displayName = manifest.assets.models[objDef.model]?.displayName;
      const label = displayName ? `${displayName} (${objDef.model})` : objDef.model;
      console.warn(`Model '${label}' not found in assets`);
      continue;
    }

    // Static meshes have _MODEL_TRANSFORM and _MESH_TRANSFORM baked into vertex
    // positions by `resolveSceneMeshes` — applying them again via obj.trs would
    // double-transform. Animated meshes use rawMeshes (no bake), so the
    // skinning shader applies node TRS via joint matrices and obj.trs carries
    // the asset/mesh transforms unchanged.
    const animated = !!objDef.animation;
    let assetTransform = manifest.assets.models[objDef.model]?.transform;
    if (!assetTransform && animated) {
      const glbTransform = readModelTransform(model.json);
      if (glbTransform) {
        assetTransform = {
          position: glbTransform.position,
          rotation: glbTransform.rotation,
          scale: glbTransform.scale,
        };
      }
    }

    const objects: RenderObject[] = [];

    const meshesToUse = animated ? model.rawMeshes : model.meshes;
    const meshNameMap = animated ? model.rawMeshByName : model.meshByName;

    // "*" = load all meshes (for multi-mesh static models like Sponza)
    const meshList =
      objDef.meshName === '*'
        ? meshesToUse
        : (() => {
            const m = meshNameMap.get(objDef.meshName);
            if (!m) {
              console.warn(`Mesh '${objDef.meshName}' not found in model '${objDef.model}'`);
              return [];
            }
            return [m];
          })();

    for (const resolved of meshList) {
      // Only read _MESH_TRANSFORM for animated meshes — for static meshes it's
      // already baked into vertices by `resolveSceneMeshes`.
      const meshTransform = animated ? readMeshTransform(model.json, resolved.name) : null;

      for (let j = 0; j < resolved.primitives.length; j++) {
        const prim = resolved.primitives[j];

        // Static non-animated objects sharing the same model+meshName+primitive
        // can reuse the same Mesh (and thus the same GL buffers) for instancing.
        const canShareMesh = objDef.render?.static && !objDef.animation;
        const meshCacheKey = `${objDef.model}:${resolved.name}:${j}`;
        let mesh: GltfMesh;

        if (canShareMesh && meshCache.has(meshCacheKey)) {
          mesh = meshCache.get(meshCacheKey)!;
        } else {
          const name = canShareMesh ? meshCacheKey : `${objDef.name}_${resolved.name}_${j}`;
          mesh = new GltfMesh(name, scene.ctx, scene, prim);

          if (objDef.animation) {
            const anim = new GltfAnimation(model.json, model.binaries);
            anim.preComputeAnimations();
            mesh.animationDataTexture = new GltfAnimationDataTexture(scene.ctx, anim);
            mesh.animation = anim;
          }

          if (canShareMesh) {
            meshCache.set(meshCacheKey, mesh);
          }
        }

        const obj = scene.objectFactory.instantiateMesh(mesh);
        setupObject(scene, obj, objDef, prim, model, assetTransform, meshTransform);

        scene.instanceManager.registerObject(obj);
        objects.push(obj);
      }
    }

    // Load attachments for this object
    if (objDef.attachments && objects.length > 0) {
      const primaryObj = objects[0];
      loadAttachments(scene, primaryObj, objDef, modelCache);
    }

    namedObjects.set(objDef.name, objects);
  }

  return namedObjects;
}

function setupObject(
  scene: Scene,
  obj: RenderObject,
  objDef: SceneObjectDef,
  prim: ResolvedPrimitive,
  model: ParsedModel,
  assetTransform?: TransformDef,
  meshTransform?: { position?: number[]; rotation?: number[]; scale?: number } | null,
): void {
  applyTransform(obj, assetTransform, meshTransform, objDef.transform);
  applyRender(scene, obj, objDef);

  if (objDef.materialOverrides?.texture) {
    const overrideMat = new Material();
    overrideMat.baseColor = scene.textureCache.getTexture(objDef.materialOverrides.texture);
    obj.material = overrideMat;
  } else if (prim.materialIndex !== undefined && model.materials.has(prim.materialIndex)) {
    obj.material = model.materials.get(prim.materialIndex);
  }

  if (objDef.animation?.initialState) {
    obj.animationState = createAnimationState(obj, model.json);
    obj.animationState.switchState(objDef.animation.initialState);
  }
}

/**
 * Three-layer transform composition: asset → mesh → object. Layering rules
 * (overwrite for position, multiply for scale, compose for rotation) are
 * documented canonically in {@link ../../util/PlacementComposer.ts}; this
 * function is the runtime implementation that writes the result into a
 * `RenderObject`'s `TRS` and captures the rest orientation as
 * `fixedRotationQuat` for movement code.
 */
export function applyTransform(
  obj: RenderObject,
  assetTransform?: TransformDef,
  meshTransform?: { position?: number[]; rotation?: number[]; scale?: number } | null,
  objectTransform?: TransformDef,
): void {
  let baseScale = 1;
  const layers: quat[] = [];

  if (assetTransform) {
    if (assetTransform.rotation) {
      layers.push(rotationAsQuat(assetTransform.rotation));
    }
    if (assetTransform.scale !== undefined) {
      baseScale = assetTransform.scale;
    }
  }

  if (meshTransform) {
    if (meshTransform.position) {
      obj.trs.position = meshTransform.position;
    }
    if (meshTransform.rotation) {
      layers.push(rotationAsQuat(meshTransform.rotation));
    }
    if (meshTransform.scale !== undefined) {
      baseScale *= meshTransform.scale;
    }
  }

  if (objectTransform) {
    if (objectTransform.position) {
      obj.trs.position = objectTransform.position;
    }
    if (objectTransform.rotation) {
      layers.push(rotationAsQuat(objectTransform.rotation));
    }
    if (objectTransform.scale !== undefined) {
      baseScale *= objectTransform.scale;
    }
  }

  obj.trs.scale = baseScale;
  const restRotation = composeWorldRotation(layers);
  obj.trs.setRotationQuat(restRotation);

  // Preserve the rest orientation so rotateTo() (used by movement) composes
  // a yaw on top instead of overwriting the asset's base X/Y/Z tilt.
  quat.copy(obj.fixedRotationQuat, restRotation);
}

function rotationAsQuat(rotation: number[]): quat {
  if (rotation.length !== 4) {
    throw new Error(
      `Manifest rotation must be a 4-element quaternion [x, y, z, w], got ${rotation.length} elements`,
    );
  }
  return quat.fromValues(rotation[0], rotation[1], rotation[2], rotation[3]);
}

function applyRender(
  scene: Scene,
  obj: RenderObject,
  objDef: { render?: { lightning?: string[]; static?: boolean } },
): void {
  if (objDef.render?.lightning) {
    obj.renderConfiguration.lightning = objDef.render.lightning as (
      | 'static'
      | 'dynamic'
      | 'baked'
    )[];
  }
  if (objDef.render?.static !== undefined) {
    obj.renderConfiguration.static = objDef.render.static;
    // The non-static depth-caster cache is built when the obj was added
    // (where its `static` flag was still default). Mutating it here makes
    // that cache stale; explicitly invalidate so the next shadow frame
    // rebuilds with the correct membership.
    scene.objectManager.invalidateNonStaticCache();
  }
}

function loadAttachments(
  scene: Scene,
  parentObj: RenderObject,
  objDef: SceneObjectDef,
  modelCache: Map<string, ParsedModel>,
): void {
  const holder = new GltfAttachmentHolder(parentObj);
  parentObj.attachments = holder;

  // Read per-(bone, model, mesh) overrides and per-bone defaults from the animated model's GLB
  const parentModel = modelCache.get(objDef.model);
  const overrides = parentModel ? readJointAttachments(parentModel.json) : [];
  const overrideMap = new Map(
    overrides
      .filter((a) => a.transform)
      .map((a) => [`${a.bone}:${a.model}:${a.mesh}`, a.transform!]),
  );
  const boneDefaults = parentModel ? readAttachmentBoneDefaults(parentModel.json) : [];
  const boneDefaultMap = new Map(boneDefaults.map((d) => [d.bone, d.transform]));

  for (const [boneName, attachDef] of Object.entries(objDef.attachments!)) {
    warnIfNotUuid(attachDef.model, `attachment on '${objDef.name}' bone '${boneName}'`);
    const attachModel = modelCache.get(attachDef.model);
    if (!attachModel) {
      console.warn(`Attachment model '${attachDef.model}' not found in assets`);
      continue;
    }

    // Use rawMeshes: skinned model vertices must stay in bind-pose space
    // since no skinning shader runs on the attachment
    const resolved = attachModel.rawMeshByName.get(attachDef.meshName);
    if (!resolved) {
      console.warn(
        `Attachment mesh '${attachDef.meshName}' not found in model '${attachDef.model}'`,
      );
      continue;
    }

    for (let j = 0; j < resolved.primitives.length; j++) {
      const prim = resolved.primitives[j];
      const name = `${objDef.name}_attach_${boneName}_${j}`;
      const mesh = new GltfMesh(name, scene.ctx, scene, prim);
      const obj = scene.objectFactory.instantiateMesh(mesh);

      if (attachDef.materialOverrides?.texture) {
        const overrideMat = new Material();
        overrideMat.baseColor = scene.textureCache.getTexture(
          attachDef.materialOverrides.texture,
        );
        obj.material = overrideMat;
      } else if (
        prim.materialIndex !== undefined &&
        attachModel.materials.has(prim.materialIndex)
      ) {
        obj.material = attachModel.materials.get(prim.materialIndex);
      }

      const attachment = holder.setAttachment(boneName, obj);

      // Resolution order:
      // 1. Per-scene-object manifest override (attachDef.transform, rarely used)
      // 2. Per-(bone, model, mesh) override on the parent animated model
      // 3. boneDefault × attachmentModelTransform (composed fallback)
      const manifestOverride = attachDef.transform;
      const perAttachmentOverride = overrideMap.get(
        `${boneName}:${attachDef.model}:${attachDef.meshName}`,
      );

      if (manifestOverride) {
        buildAttachmentOffsetMatrix(attachment.offsetMatrix, {
          position: (manifestOverride.position ?? [0, 0, 0]) as [number, number, number],
          rotation: (manifestOverride.rotation ?? [0, 0, 0, 1]) as [number, number, number, number],
          scale: manifestOverride.scale ?? 1,
        });
      } else if (perAttachmentOverride) {
        buildAttachmentOffsetMatrix(attachment.offsetMatrix, perAttachmentOverride);
      } else {
        composeAttachmentOffsetMatrix(
          attachment.offsetMatrix,
          boneDefaultMap.get(boneName) ?? null,
          readModelTransform(attachModel.json),
          readMeshTransform(attachModel.json, attachDef.meshName),
        );
      }
    }
  }
}
